export const CONTRACTS = Object.freeze({
  service: 'service-intake-request-v1',
  builder: 'assisted-build-draft-v1',
});

export const EDGE_FUNCTIONS = Object.freeze({
  [CONTRACTS.service]: 'service-intake',
  [CONTRACTS.builder]: 'assisted-build',
});

export const PUBLIC_ERROR_MESSAGES = Object.freeze({
  ORIGIN_FORBIDDEN: 'Este canal todavía no está autorizado.',
  METHOD_NOT_ALLOWED: 'No pudimos procesar esta solicitud.',
  CONTENT_TYPE_INVALID: 'No pudimos validar esta solicitud.',
  PAYLOAD_TOO_LARGE: 'La información supera el tamaño permitido.',
  INVALID_JSON: 'No pudimos validar esta solicitud.',
  CONTRACT_INVALID: 'Revisá los datos ingresados e intentá nuevamente.',
  BOT_REJECTED: 'No pudimos completar la verificación de seguridad.',
  STATUS_ACCESS_DENIED: 'No fue posible consultar el estado.',
  IDEMPOTENCY_COLLISION: 'La solicitud cambió. Volvé a enviarla.',
  RATE_LIMITED: 'Hay demasiados intentos. Esperá unos minutos.',
  SERVICE_UNAVAILABLE: 'El servicio no está disponible temporalmente.',
  NETWORK_UNAVAILABLE: 'El navegador no pudo conectar con Operations. Conservamos tus datos en el formulario para que puedas reintentar.',
  INTERNAL_ERROR: 'No pudimos procesar la solicitud.',
});

function text(data, field) {
  return String(data.get(field) ?? '').trim();
}

function optionalText(data, field) {
  return text(data, field) || null;
}

function stringList(data, field) {
  const values = text(data, field)
    .split(/[\n,]/u)
    .map((value) => value.trim())
    .filter(Boolean);
  const uniqueValues = [...new Set(values)];
  if (uniqueValues.length > 30 || uniqueValues.some((value) => value.length > 120)) {
    throw new TypeError(`${field} no cumple el contrato`);
  }
  return uniqueValues;
}

function maximumAmount(data) {
  const rawValue = text(data, 'budgetArs');
  if (!/^[1-9][0-9]*$/u.test(rawValue)) {
    throw new TypeError('budgetArs no cumple el contrato');
  }
  return `${rawValue}.00`;
}

function desiredBy(data) {
  const rawValue = optionalText(data, 'desiredBy');
  if (rawValue === null) return null;
  const value = new Date(rawValue);
  if (Number.isNaN(value.valueOf())) throw new TypeError('desiredBy no cumple el contrato');
  return value.toISOString();
}

export function buildContractPayload(contractVersion, data, submission) {
  const contact = {
    name: text(data, 'fullName'),
    channel: text(data, 'contactPreference'),
    value: text(data, 'contactValue'),
  };

  if (contractVersion === CONTRACTS.service) {
    return {
      contractVersion,
      idempotencyKey: submission.idempotencyKey,
      submittedAt: submission.createdAt,
      contact,
      equipment: {
        type: text(data, 'equipmentType'),
        brandModel: optionalText(data, 'brandModel'),
      },
      reportedProblem: text(data, 'reportedProblem'),
      declaredAccessories: stringList(data, 'declaredAccessories'),
      preferredContact: contact.channel,
      consent: {
        privacyAccepted: data.get('privacyAccepted') === 'accepted',
        marketingAccepted: false,
      },
      attribution: null,
    };
  }

  if (contractVersion === CONTRACTS.builder) {
    return {
      contractVersion,
      draftId: submission.draftId,
      idempotencyKey: submission.idempotencyKey,
      createdAt: submission.createdAt,
      use: [text(data, 'useCase')],
      budget: {
        currency: 'ARS',
        maximumAmount: maximumAmount(data),
      },
      applications: stringList(data, 'applications'),
      preferences: stringList(data, 'preferences'),
      peripherals: text(data, 'peripherals') === 'true',
      operatingSystem: text(data, 'operatingSystem'),
      desiredBy: desiredBy(data),
      contact,
      status: 'pending-review',
    };
  }

  throw new TypeError(`Contrato público no soportado: ${contractVersion}`);
}

export class SubmissionIdentity {
  #crypto;
  #now;
  #value;

  constructor(cryptoApi = globalThis.crypto, now = Date.now) {
    this.#crypto = cryptoApi;
    this.#now = now;
  }

  current(contractVersion) {
    if (this.#value?.contractVersion === contractVersion) return this.#value;

    const uuid = this.#crypto.randomUUID().toLowerCase();
    const namespace = contractVersion === CONTRACTS.service ? 'service' : 'build';
    this.#value = {
      contractVersion,
      idempotencyKey: `web:${namespace}:${uuid}`,
      createdAt: new Date(this.#now()).toISOString(),
      ...(contractVersion === CONTRACTS.builder
        ? { draftId: `abd_${uuid.replaceAll('-', '')}` }
        : {}),
    };
    return this.#value;
  }

  invalidate() {
    this.#value = undefined;
  }
}

export function buildRequestHeaders({ formStartedAt, honeypot = '', turnstileToken = '' }) {
  return {
    'Content-Type': 'application/json',
    'X-Cyclon-Form-Started-At': String(formStartedAt),
    'X-Cyclon-Honeypot': honeypot,
    ...(turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : {}),
  };
}

export function buildRequestOptions(payload, security) {
  return {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    redirect: 'error',
    referrerPolicy: 'strict-origin-when-cross-origin',
    headers: buildRequestHeaders(security),
    body: JSON.stringify(payload),
  };
}

export function matchesEdgeFunctionEndpoint(endpointValue, edgeFunction) {
  try {
    const endpoint = new URL(endpointValue);
    if (
      endpoint.protocol !== 'https:'
      || endpoint.username
      || endpoint.password
      || endpoint.search
      || endpoint.hash
    ) {
      return false;
    }
    return endpoint.pathname.replace(/\/+$/u, '').endsWith(`/${edgeFunction}`);
  } catch {
    return false;
  }
}

export function isValidSuccessResponse(edgeFunction, status, payload) {
  if (![200, 201].includes(status) || !payload || typeof payload !== 'object') return false;

  if (edgeFunction === 'service-intake') {
    return payload.contractVersion === 'service-intake-response-v1'
      && /^svc_pub_[A-Z0-9]{12,32}$/u.test(payload.publicReference)
      && /^svc_tok_[A-Za-z0-9_-]{43}$/u.test(payload.publicStatusToken)
      && payload.status === 'awaiting-confirmation';
  }

  if (edgeFunction === 'assisted-build') {
    return payload.contractVersion === 'assisted-build-response-v1'
      && /^abd_[a-z0-9]{20,40}$/u.test(payload.draftId)
      && payload.status === 'pending-review';
  }

  return false;
}

export class CyclonPublicForm {
  constructor(form, dependencies = {}) {
    this.form = form;
    this.status = form.querySelector('[data-form-status]');
    this.fetch = dependencies.fetch ?? globalThis.fetch;
    this.now = dependencies.now ?? Date.now;
    this.identity = new SubmissionIdentity(dependencies.crypto ?? globalThis.crypto, this.now);
    this.formStartedAt = this.now();
    this.turnstileWidgetId = null;
    this.form.addEventListener('input', () => this.identity.invalidate());
    this.form.addEventListener('submit', (event) => this.submit(event));
    this.initializeTurnstile();
  }

  initializeTurnstile(attempt = 0) {
    const container = this.form.querySelector('[data-cyclon-turnstile]');
    const tokenInput = this.form.querySelector('[data-turnstile-token]');
    const sitekey = container?.dataset.sitekey?.trim();
    if (!container || !tokenInput || !sitekey) return;

    if (!globalThis.turnstile?.render) {
      if (attempt < 100) {
        globalThis.setTimeout(() => this.initializeTurnstile(attempt + 1), 100);
      }
      return;
    }

    this.turnstileWidgetId = globalThis.turnstile.render(container, {
      sitekey,
      callback: (token) => {
        tokenInput.value = token;
      },
      'expired-callback': () => {
        tokenInput.value = '';
      },
      'error-callback': () => {
        tokenInput.value = '';
      },
    });
  }

  resetTurnstile() {
    const tokenInput = this.form.querySelector('[data-turnstile-token]');
    if (tokenInput) tokenInput.value = '';
    if (this.turnstileWidgetId !== null && globalThis.turnstile?.reset) {
      globalThis.turnstile.reset(this.turnstileWidgetId);
    }
  }

  endpoint() {
    const endpoint = this.form.dataset.endpoint;
    const edgeFunction = this.form.dataset.edgeFunction;
    return matchesEdgeFunctionEndpoint(endpoint, edgeFunction) ? endpoint : null;
  }

  async submit(event) {
    event.preventDefault();
    const endpoint = this.endpoint();
    if (!endpoint) {
      this.setStatus('Este formulario todavía no tiene un endpoint válido.', true);
      return;
    }

    const button = this.form.querySelector('[type="submit"]');
    const contractVersion = this.form.dataset.contractVersion;
    const edgeFunction = this.form.dataset.edgeFunction;
    if (!button || EDGE_FUNCTIONS[contractVersion] !== edgeFunction) {
      this.setStatus('No pudimos validar este formulario.', true);
      return;
    }

    let payload;
    try {
      payload = buildContractPayload(
        contractVersion,
        new FormData(this.form),
        this.identity.current(contractVersion),
      );
    } catch {
      this.setStatus('Revisá los datos ingresados e intentá nuevamente.', true);
      return;
    }

    const honeypot = this.form.querySelector('[data-cyclon-honeypot]')?.value.trim() ?? '';
    const turnstileToken = this.form.querySelector('[data-turnstile-token]')?.value.trim() ?? '';
    if (this.form.querySelector('[data-cyclon-turnstile]') && !turnstileToken) {
      this.setStatus(PUBLIC_ERROR_MESSAGES.BOT_REJECTED, true);
      return;
    }
    button.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    this.setStatus('Enviando solicitud…');

    try {
      const response = await this.fetch(endpoint, buildRequestOptions(
        payload,
        {
          formStartedAt: this.formStartedAt,
          honeypot,
          turnstileToken,
        },
      ));
      const responsePayload = await response.json().catch(() => null);

      if (!isValidSuccessResponse(edgeFunction, response.status, responsePayload)) {
        const errorCode = responsePayload?.contractVersion === 'public-api-error-v1'
          ? responsePayload.code
          : 'INTERNAL_ERROR';
        if (errorCode === 'IDEMPOTENCY_COLLISION') this.identity.invalidate();
        throw new Error(errorCode);
      }

      this.form.reset();
      this.identity.invalidate();
      this.formStartedAt = this.now();
      this.resetTurnstile();
      const reference = edgeFunction === 'service-intake'
        ? responsePayload.publicReference
        : responsePayload.draftId;
      this.setStatus(`Recibimos tu solicitud. Referencia: ${reference}. Te vamos a contactar para continuar.`);
    } catch (error) {
      const errorCode = error instanceof TypeError
        ? 'NETWORK_UNAVAILABLE'
        : error instanceof Error
          ? error.message
          : 'INTERNAL_ERROR';
      if (errorCode === 'BOT_REJECTED') this.resetTurnstile();
      this.setStatus(
        PUBLIC_ERROR_MESSAGES[errorCode] ?? PUBLIC_ERROR_MESSAGES.INTERNAL_ERROR,
        true,
      );
    } finally {
      button.disabled = false;
      this.form.removeAttribute('aria-busy');
    }
  }

  setStatus(message, error = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.error = String(error);
  }
}

if (typeof document !== 'undefined') {
  document
    .querySelectorAll('[data-cyclon-public-form]')
    .forEach((form) => new CyclonPublicForm(form));
}
